import { apiJson } from './client';

const DEFAULT_API_BASE = 'https://tutor-gateway-staging.autoscuola-ai.workers.dev';
export const QWEN_REALTIME_DEFAULT_VOICE = 'Dolce';

export interface QwenRealtimeUrlOptions {
  apiBase?: string;
  ticket: string;
}

export interface QwenRealtimeTicketOptions {
  voice?: string;
  model?: string;
  sessionId?: string;
  context?: VoiceContextV1;
}

export interface QwenRealtimeTicketResponse {
  ticket: string;
  wsUrl: string;
  sessionId: string;
  expiresAt: string;
  model: string;
  voice: string;
}

export interface VoiceContextV1 {
  version: 1;
  locale: 'it-IT';
  surface: 'prof' | 'studia' | 'progressi';
  route: string;
  topic?: {
    id?: string;
    label: string;
    source: 'route' | 'drawer' | 'progressi';
  };
  weakAreaLabel?: string;
  visiblePrompt?: {
    kind: 'question' | 'card' | 'exercise';
    text: string;
    choices?: string[];
    imageId?: string;
    imageAlt?: string;
  };
  currentVisibleImages?: Array<{
    kind: 'sign' | 'question';
    id: string;
    alt?: string;
  }>;
  /**
   * Voice-visuals rebuild P1a — the VISUAL-STATE ECHO (RC4). The FE's ground
   * truth about what the student is seeing THIS turn, derived from the single
   * current visual set: `shown_new` (a new set was just rendered), `unchanged`
   * (the same set is still on screen), `none` (nothing is shown). The BE echoes
   * it into the session context so a "non mi hai mostrato il segnale" complaint
   * is answered with ground truth instead of a reflexive "Hai ragione, scusami".
   */
  renderStatus?: 'shown_new' | 'unchanged' | 'none';
  /** Founder decision A (2026-06-03): short client-built rolling summary of the
   *  conversation so far, re-sent across the ~2-min socket renewal so Il Prof
   *  keeps continuity. Built from spoken transcripts only; capped; no LLM call. */
  conversationSummary?: string;
}

export interface QwenRealtimeUsage {
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  input_tokens_details?: Partial<Record<'audio_tokens' | 'image_tokens' | 'text_tokens' | 'video_tokens', number>>;
  output_tokens_details?: Partial<Record<'audio_tokens' | 'image_tokens' | 'text_tokens' | 'video_tokens', number>>;
}

export interface PersistQwenVoiceTurnBody {
  sessionId: string;
  turnClientId: string;
  userText: string;
  assistantText: string;
  providerResponseId?: string;
  providerUsage?: QwenRealtimeUsage;
  providerUsageHash?: string;
  turnProof: string;
}

export interface PersistQwenVoiceTurnResponse {
  ok: true;
  duplicate: boolean;
  sessionId: string;
  userMessageId: string;
  assistantMessageId: string;
}

export function qwenRealtimeVoiceEnabled(): boolean {
  return import.meta.env.VITE_QWEN_REALTIME_VOICE === 'true';
}

export function buildQwenRealtimeUrl({
  apiBase = import.meta.env.VITE_API_BASE || DEFAULT_API_BASE,
  ticket,
}: QwenRealtimeUrlOptions): string {
  if (!ticket) throw new Error('missing_voice_ticket');
  const base = new URL(apiBase);
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  base.pathname = '/api/voice/qwen/realtime';
  base.search = '';
  base.searchParams.set('ticket', ticket);
  return base.toString();
}

export async function requestQwenRealtimeTicket({
  voice = QWEN_REALTIME_DEFAULT_VOICE,
  model,
  sessionId,
  context,
}: QwenRealtimeTicketOptions = {}): Promise<QwenRealtimeTicketResponse> {
  return apiJson<QwenRealtimeTicketResponse>('/api/voice/qwen/ticket', {
    method: 'POST',
    body: JSON.stringify({
      voice,
      ...(model ? { model } : {}),
      ...(sessionId ? { sessionId } : {}),
      ...(context ? { context } : {}),
    }),
  });
}

export async function persistQwenRealtimeVoiceTurn(
  body: PersistQwenVoiceTurnBody,
): Promise<PersistQwenVoiceTurnResponse> {
  return apiJson<PersistQwenVoiceTurnResponse>('/api/voice/qwen/turns', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

function clampSample(sample: number): number {
  if (sample > 1) return 1;
  if (sample < -1) return -1;
  return sample;
}

export function downsampleFloat32ToInt16Pcm(
  input: Float32Array,
  inputSampleRate: number,
  outputSampleRate = 16000,
): Int16Array {
  if (inputSampleRate === outputSampleRate) {
    return float32ToInt16(input);
  }

  const ratio = inputSampleRate / outputSampleRate;
  const outputLength = Math.floor(input.length / ratio);
  const output = new Int16Array(outputLength);
  for (let i = 0; i < outputLength; i += 1) {
    const sourceIndex = Math.floor(i * ratio);
    const sample = clampSample(input[sourceIndex] ?? 0);
    output[i] = sample < 0 ? Math.round(sample * 32768) : Math.round(sample * 32767);
  }
  return output;
}

export function float32ToInt16(input: Float32Array): Int16Array {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    const sample = clampSample(input[i] ?? 0);
    output[i] = sample < 0 ? Math.round(sample * 32768) : Math.round(sample * 32767);
  }
  return output;
}

export function int16ArrayToBase64(input: Int16Array): string {
  const bytes = new Uint8Array(input.buffer, input.byteOffset, input.byteLength);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function base64PcmToFloat32(input: string): Float32Array {
  const binary = atob(input);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const samples = new Int16Array(bytes.buffer);
  const output = new Float32Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) output[i] = samples[i] / 32768;
  return output;
}
